import { TableWithStoredSettingsAndLayoutSettings } from '@Components/Tables/TableWithStoredSettingsAndLayoutSettings';
import { Permission } from '@Constants/Permission.g';
import { initialInvoiceSorting, openInvoiceColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { useLoadUnpaidInvoices, useMarkInvoicesAsPaid, useSetAdmonition } from '@Hooks/Billing';
import {
    Badge,
    Button,
    defaultIconSize,
    FloatingToolbar,
    InputPromptModal,
    SingleSelect,
    type ToolbarOptions,
    useTableRefetch,
    toCalendarDay,
} from '@synqo/frontend-component-library';
import { IconCircleCheck, IconMessageReport } from '@tabler/icons-react';
import { type ApiInvoice, EnumFrontendLayoutSetting } from '@Model';
import { useCallback, useMemo, useState } from 'react';
import { InvoiceTableModes } from '@Modules/Billing';
import { IHasTableModes } from '@Hooks/useTableModes';

interface IFrameListOpenInvoicesProps extends IHasTableModes<InvoiceTableModes> {}

const emptyItems: ApiInvoice[] = [];

export function FrameListOpenInvoices({
    tableModeValue,
    setTableMode,
    availableTableModes,
}: IFrameListOpenInvoicesProps) {
    const hasPermission = useHasPermission();

    const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const { refetchRef, refetchTable } = useTableRefetch();

    const hasSelectedInvoices = selectedInvoiceIds.length > 0;

    const listApi = useLoadUnpaidInvoices();
    const { markAsPaid, isPending: isMarkAsPaidPending } = useMarkInvoicesAsPaid(selectedInvoiceIds);
    const { setAdmonitionDate, isPending: isMarkAsAdmonishedPending } = useSetAdmonition(selectedInvoiceIds);

    const resetTable = useCallback(() => {
        refetchTable();
        setSelectedInvoiceIds([]);
    }, [refetchTable]);

    const admonish = useCallback(async () => {
        await setAdmonitionDate();
        resetTable();
    }, [setAdmonitionDate, resetTable]);

    const openPaymentModal = useCallback(() => setIsPaymentModalOpen(true), []);
    const closePaymentModal = useCallback(() => setIsPaymentModalOpen(false), []);

    const handleConfirmPayment = useCallback(
        (paymentDate: unknown) => {
            if (typeof paymentDate !== 'string') return;
            closePaymentModal();
            markAsPaid(paymentDate).then(resetTable);
        },
        [closePaymentModal, markAsPaid, resetTable],
    );

    const objectSelector = useMemo(
        () => ({
            locked: isMarkAsPaidPending || isMarkAsAdmonishedPending,
            selectedObjectIds: selectedInvoiceIds,
            setSelectedObjectIds: setSelectedInvoiceIds,
        }),
        [isMarkAsPaidPending, isMarkAsAdmonishedPending, selectedInvoiceIds],
    );

    const toolbarElements = useCallback(
        (elements: ToolbarOptions[]) => [
            ...elements,
            <SingleSelect<InvoiceTableModes>
                key="tableMode"
                name="tableMode"
                items={availableTableModes}
                value={tableModeValue}
                onChange={setTableMode}
            />,
        ],
        [setTableMode, availableTableModes, tableModeValue],
    );

    const modalConfirmProp = useMemo(
        () => ({
            variant: 'primary' as const,
            onClick: handleConfirmPayment,
        }),
        [handleConfirmPayment],
    );

    return (
        <>
            <TableWithStoredSettingsAndLayoutSettings
                layoutSettingsContext={EnumFrontendLayoutSetting.OpenInvoicesTableLayout}
                items={emptyItems}
                name={'OposInvoiceList'}
                columns={openInvoiceColumns}
                loader={listApi.loadPage}
                refetchRef={refetchRef}
                canCreate={hasPermission(Permission.BillingInvoicesCreate)}
                objectSelector={objectSelector}
                initialSorting={initialInvoiceSorting}
                toolbarElements={toolbarElements}
                useOnClickNavigation
                fullSize
            />
            <FloatingToolbar isOpen={hasSelectedInvoices}>
                <Badge size="sm" type="input" variant="primary">
                    {selectedInvoiceIds.length.toString()}
                </Badge>
                Ausgewählt
                <Button appearance="filled" variant="success" onClick={openPaymentModal}>
                    <IconCircleCheck size={defaultIconSize} />
                    &nbsp;Als bezahlt markieren
                </Button>
                <Button appearance="filled" variant="warning" onClick={admonish}>
                    <IconMessageReport size={defaultIconSize} />
                    &nbsp;Mahnverfahren einleiten
                </Button>
            </FloatingToolbar>
            <InputPromptModal
                name="PaymentDateModal"
                isOpen={isPaymentModalOpen}
                label={'Bezahlt am'}
                value={toCalendarDay(new Date())}
                inputType="day"
                required
                title={'Datum der Zahlung wählen'}
                message={`${selectedInvoiceIds.length} Rechnung(en) ausgewählt`}
                confirm={modalConfirmProp}
                onClose={closePaymentModal}
                disableAutoFocus={true}
            />
        </>
    );
}
